/**
 * Object URLs for market-conditions screenshots, cached by image id.
 *
 * The route is permission-gated, so every surface fetches the blob through the
 * authenticated client. Without a cache, paging a carousel refetched each tile
 * on every click -- and paging back refetched images that had just been
 * revoked -- which showed as a flash of empty placeholders.
 *
 * Entries are immutable: a saved image is never edited, only deleted, and a
 * deleted id is simply never asked for again.
 */
import api from './api';

/** Plenty for every market's screenshots; bounded so a long session cannot grow forever. */
const MAX_ENTRIES = 60;

const urls = new Map<number, string>();
const inFlight = new Map<number, Promise<string>>();

export function cachedImageUrl(id: number): string | undefined {
  return urls.get(id);
}

export function loadImageUrl(id: number): Promise<string> {
  const ready = urls.get(id);
  if (ready) return Promise.resolve(ready);
  const existing = inFlight.get(id);
  if (existing) return existing;

  const request = api.get(`/market-conditions/images/${id}`, { responseType: 'blob' })
    .then(({ data }) => {
      const url = URL.createObjectURL(data as Blob);
      urls.set(id, url);
      // Evict oldest first; Map preserves insertion order.
      while (urls.size > MAX_ENTRIES) {
        const [oldest, oldestUrl] = urls.entries().next().value as [number, string];
        urls.delete(oldest);
        URL.revokeObjectURL(oldestUrl);
      }
      return url;
    })
    .finally(() => { inFlight.delete(id); });

  inFlight.set(id, request);
  return request;
}

/** Warm images the user is about to reach, so paging does not flash. */
export function prefetchImages(ids: number[]): void {
  for (const id of ids) void loadImageUrl(id).catch(() => { /* shown as a placeholder if it fails */ });
}
